import { useState, useEffect, useRef } from "react";
import { C, S, G, uid, today, fmtDate, isPast, calcGrade } from "../../utils/theme";
import { Modal, Badge, Empty, ProgressBar, PageHdr, GlassCard, LevelBadge, FeesBadge, Inp } from "../../components/UI";
import { academicAPI, assessmentAPI, commAPI, authAPI, reapplyAPI } from "../../utils/api";

// ── Course Access Logic ────────────────────────────────────────────────────
// A course belongs to a student either when it's tied to their own program,
// or when it's a "general" course shared across the whole degree (no
// program_id — every student in that degree gets it automatically).
export function isMyCourse(c, user) {
  if (c.program_id) return c.program_id === user.program_id;
  return c.level === "general" && c.degree_id === user.degree_id;
}

// Courses unlock one at a time by Order, WITHIN each level — General
// course 1 opens first, General 2 only once General 1 is passed, and so on.
// A level's own courses must ALL be passed before the next level's first
// course opens at all (a level with zero courses counts as already done,
// so e.g. a Master's program with no General courses starts straight at Core).
function getCourseAccess(user, courses, examResults) {
  const isExamPassed = (courseId) => {
    const result = examResults.find(r => r.course_id === courseId || r.exam_course_id === courseId);
    return result && result.final_score >= 60;
  };

  const myCourses = courses.filter(c => isMyCourse(c, user));

  const byLevelSorted = (level) => myCourses
    .filter(c => c.level === level)
    .slice()
    .sort((a, b) => (a.order ?? 1) - (b.order ?? 1) || new Date(a.created_at) - new Date(b.created_at));

  const sequence = (list, startUnlocked) => {
    let chainOpen = startUnlocked;
    return list.map(c => {
      const unlocked = chainOpen;
      const passed = isExamPassed(c.id);
      if (!passed) chainOpen = false;
      return { ...c, unlocked, passed };
    });
  };
  const isLevelDone = (list) => list.length === 0 || list.every(c => c.passed);

  const general = sequence(byLevelSorted("general"), true);
  const generalDone = isLevelDone(general);
  const core = sequence(byLevelSorted("core"), generalDone);
  const coreDone = isLevelDone(core);
  const specialist = sequence(byLevelSorted("specialist"), coreDone);
  const specialistDone = isLevelDone(specialist);

  const byLevel = (list, complete) => ({ courses: list, unlocked: list.some(c => c.unlocked), complete });

  return {
    all: [...general, ...core, ...specialist],
    general: byLevel(general, generalDone && general.length > 0),
    core: byLevel(core, coreDone && core.length > 0),
    specialist: byLevel(specialist, specialistDone && specialist.length > 0),
    isExamPassed,
  };
}

// ── DASHBOARD ──────────────────────────────────────────────────────────────
export function StudentDashboard({ user, setPage }) {
  const [courses, setCourses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [examResults, setExamResults] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [c, a, s, r, ann] = await Promise.all([
          academicAPI.getCourses(),
          assessmentAPI.getAssignments(),
          assessmentAPI.mySubmissions(),
          assessmentAPI.myResults(),
          commAPI.getAnnouncements(),
        ]);
        setCourses(c.data||[]);
        setAssignments(a.data||[]);
        setSubmissions(s.data||[]);
        setExamResults(r.data||[]);
        setAnnouncements((ann.data||[]).slice(0,3));
      } catch(e) { console.error(e); }
    };
    load();
  }, []);

  const myCourses = user.program_id ? courses.filter(c => isMyCourse(c, user)) : [];
  const myAssignments = assignments.filter(a => myCourses.some(c => c.id === a.course_id));
  const pendingAssign = myAssignments.filter(a => !submissions.find(s => s.assignment_id === a.id) && !isPast(a.due_date));
  const passedExams = examResults.length;
  const totalExams = myCourses.length;
  const progress = totalExams > 0 ? Math.round((passedExams / totalExams) * 100) : 0;

  const access = user.program_id ? getCourseAccess(user, courses, examResults) : null;

  return (
    <div>
      {/* Hero Profile Card */}
      <div style={{ background:G.hero, borderRadius:20, padding:"28px 32px", marginBottom:28, display:"flex", gap:28, alignItems:"center", border:`1px solid ${C.border}`, position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", top:-30, right:-30, width:200, height:200, borderRadius:"50%", background:"rgba(27,58,107,0.06)", pointerEvents:"none" }} />
        <div style={{ position:"relative", flexShrink:0 }}>
  <div style={{ width:90, height:90, borderRadius:"50%", background:G.purple, display:"flex", alignItems:"center", justifyContent:"center", fontSize:40, overflow:"hidden", border:"3px solid rgba(139,92,246,0.35)", boxShadow:"0 4px 12px rgba(139,92,246,0.25)" }}>
    {user.photo ? <img src={user.photo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : "👤"}
  </div>
  <label style={{ position:"absolute", bottom:0, right:0, width:28, height:28, background:G.blue, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", border:`2px solid ${C.surface}`, fontSize:14 }}>
    📷
    <input type="file" accept="image/*" style={{ display:"none" }} onChange={async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const fd = new FormData();
        fd.append("photo", file);
        const { usersAPI } = await import("../../utils/api");
        await usersAPI.updateStudent(user.id, fd);
        window.location.reload();
      } catch(err) { alert(err.message); }
    }} />
  </label>
</div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:11, color:C.textMuted, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>Welcome back</div>
          <div style={{ fontSize:24, fontWeight:800, color:C.text, marginBottom:4 }}>{user.name}</div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:14 }}>
            <Badge type="info">🪪 {user.student_id}</Badge>
            {user.degree_name && <Badge type="purple">{user.degree_icon} {user.short_name}</Badge>}
            {user.program_name && <Badge type="gray">{user.program_name}</Badge>}
            <FeesBadge status={user.fees_status||"unpaid"} />
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ flex:1, maxWidth:300 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6, fontSize:12, color:C.textMuted }}>
                <span>Academic Progress</span><span>{progress}%</span>
              </div>
              <div style={{ height:8, background:C.surfaceAlt, borderRadius:4, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${progress}%`, background:"linear-gradient(90deg,#1B3A6B,#8B5CF6)", borderRadius:4, transition:"width 0.6s ease" }} />
              </div>
            </div>
            <div style={{ fontSize:13, color:C.textMuted }}>{passedExams}/{totalExams} exams passed</div>
          </div>
        </div>
        {user.fees_status==="unpaid" && (
          <div style={{ background:C.roseLight, border:"1px solid #FCA5B1", borderRadius:12, padding:"14px 18px", textAlign:"center" }}>
            <div style={{ fontSize:24 }}>❌</div>
            <div style={{ fontSize:12, fontWeight:700, color:"#E11D48", marginTop:4 }}>Fees Overdue</div>
            <div style={{ fontSize:11, color:C.textMuted, marginTop:2 }}>Contact admin</div>
          </div>
        )}
        {user.fees_status==="partial" && (
          <div style={{ background:C.goldLight, border:"1px solid #F3D98B", borderRadius:12, padding:"14px 18px", textAlign:"center" }}>
            <div style={{ fontSize:24 }}>⚠️</div>
            <div style={{ fontSize:12, fontWeight:700, color:C.goldDark, marginTop:4 }}>Partial Paid</div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div style={S.statsGrid}>
        {[
          { icon:"📚", label:"My Courses", value:myCourses.length, color:"#3B82F6" },
          { icon:"📝", label:"Pending Tasks", value:pendingAssign.length, color:pendingAssign.length>0?"#F59E0B":"#10B981" },
          { icon:"✅", label:"Submitted", value:submissions.length, color:"#10B981" },
          { icon:"🎯", label:"Exams Passed", value:passedExams, color:"#8B5CF6" },
        ].map((s,i)=>(
          <div key={i} style={{ ...S.card, padding:"20px 22px", borderTop:`3px solid ${s.color}` }}>
            <div style={{ fontSize:24, marginBottom:8 }}>{s.icon}</div>
            <div style={{ fontSize:26, fontWeight:900, color:C.text }}>{s.value}</div>
            <div style={{ fontSize:12, color:"#64748B", marginTop:5 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Course Progression */}
      {access && (
        <div style={{ ...S.card, marginBottom:24, padding:"20px 24px" }}>
          <div style={{ fontWeight:700, color:C.text, marginBottom:16, fontSize:15 }}>🎓 Course Progression</div>
          <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
            {[
              { label:"General", data:access.general, color:"#3B82F6", icon:"🔵" },
              { label:"Core", data:access.core, color:"#8B5CF6", icon:"🟣" },
              { label:"Specialist", data:access.specialist, color:"#F59E0B", icon:"🟡" },
            ].filter(lv=>lv.data.courses.length>0).map(lv=>(
              <div key={lv.label} style={{ flex:1, minWidth:180, padding:"14px 16px", background:lv.data.unlocked?lv.color+"14":C.surfaceAlt, borderRadius:12, border:`1px solid ${lv.data.unlocked?lv.color+"44":C.border}`, opacity:lv.data.unlocked?1:0.6 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                  <span style={{ fontSize:20 }}>{lv.data.unlocked?lv.icon:"🔒"}</span>
                  <span style={{ fontWeight:700, color:lv.data.unlocked?C.text:C.textMuted, fontSize:14 }}>{lv.label}</span>
                  {lv.data.complete && <Badge type="success">✓ Done</Badge>}
                  {!lv.data.unlocked && <Badge type="locked">Locked</Badge>}
                </div>
                <div style={{ fontSize:12, color:C.textMuted }}>{lv.data.courses.length} courses</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
        <div style={S.card}>
          <div style={{ padding:"14px 20px", borderBottom:`1px solid ${C.border}` }}><span style={{ fontWeight:700, color:C.text }}>📢 Announcements</span></div>
          <div style={{ padding:"8px 0" }}>
            {announcements.length===0 ? <div style={{ padding:"20px", color:C.textMuted, fontSize:13, textAlign:"center" }}>No announcements</div>
              : announcements.map(a=>(
                <div key={a.id} style={{ padding:"11px 20px", borderBottom:`1px solid ${C.border}` }}>
                  <div style={{ fontWeight:600, fontSize:13, color:C.text }}>{a.title}</div>
                  <div style={{ fontSize:12, color:C.textMuted, marginTop:3, lineHeight:1.5 }}>{a.message.substring(0,80)}...</div>
                  <div style={{ fontSize:11, color:C.textFaint, marginTop:4 }}>{new Date(a.created_at).toLocaleDateString("en-PK")}</div>
                </div>
              ))
            }
          </div>
        </div>
        <div style={S.card}>
          <div style={{ padding:"14px 20px", borderBottom:`1px solid ${C.border}` }}><span style={{ fontWeight:700, color:C.text }}>⏰ Upcoming Deadlines</span></div>
          <div style={{ padding:"8px 0" }}>
            {pendingAssign.length===0 ? <div style={{ padding:"20px", color:"#10B981", fontSize:13, textAlign:"center" }}>✅ All clear!</div>
              : pendingAssign.slice(0,3).map(a=>{
                const days = Math.ceil((new Date(a.due_date)-new Date())/86400000);
                return (
                  <div key={a.id} style={{ padding:"11px 20px", borderBottom:`1px solid ${C.border}` }}>
                    <div style={{ fontWeight:600, fontSize:13, color:C.text }}>{a.title}</div>
                    <div style={{ fontSize:12, color:days<=3?"#F43F5E":"#F59E0B", marginTop:3 }}>⏰ {fmtDate(a.due_date)} ({days}d left)</div>
                  </div>
                );
              })
            }
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MY COURSES ─────────────────────────────────────────────────────────────
export function StudentCourses({ user }) {
  const [courses, setCourses] = useState([]);
  const [examResults, setExamResults] = useState([]);
  const [sel, setSel] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [c, r] = await Promise.all([academicAPI.getCourses(), assessmentAPI.myResults()]);
        setCourses(c.data||[]); setExamResults(r.data||[]);
      } catch(e) { console.error(e); }
    };
    load();
  }, []);

  const access = user.program_id ? getCourseAccess(user, courses, examResults) : null;

  if (!access) return <Empty icon="📚" text="No program assigned" sub="Contact admin to assign a program" />;

  if (sel) {
    const mods_promise = academicAPI.getModules(sel.id);
    return (
      <div>
        <button onClick={()=>setSel(null)} style={{ ...S.btn("ghost","sm"), marginBottom:20, color:"#3B82F6" }}>← Back</button>
        <div style={S.card}>
          <div style={{ padding:"22px 26px 16px", borderBottom:`1px solid ${C.border}` }}>
            <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
              <div>
                <div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:8 }}>
                  <h2 style={{ margin:0, fontSize:20, fontWeight:800, color:C.text }}>{sel.name}</h2>
                  <LevelBadge level={sel.level} />
                  <Badge type="info">{sel.code}</Badge>
                </div>
                <div style={{ fontSize:13, color:C.textMuted }}>{sel.short_name} → {sel.program_name} • {sel.credits} credits</div>
              </div>
            </div>
          </div>
          <div style={{ padding:"22px 26px" }}>
            <h3 style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:12 }}>📋 Course Outline</h3>
            <div style={{ background:C.surfaceAlt, borderRadius:12, padding:18, whiteSpace:"pre-line", fontSize:14, color:C.textMid, lineHeight:2, border:`1px solid ${C.border}` }}>
              {sel.outline || "No outline available."}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const levelConfig = [
    { key:"general",    label:"General Courses",    icon:"🔵", color:"#3B82F6", desc:"Foundation courses — complete all to unlock Core" },
    { key:"core",       label:"Core Courses",       icon:"🟣", color:"#8B5CF6", desc:"Advanced courses — complete all to unlock Specialist" },
    { key:"specialist", label:"Specialist Courses", icon:"🟡", color:"#F59E0B", desc:"Expert-level courses for your specialization" },
  ];

  return (
    <div>
      <PageHdr title="My Courses" sub={`${user.degree_name} — ${user.program_name}`} />
      {levelConfig.map(lv=>{
        const lvData = access[lv.key];
        if (lvData.courses.length===0) return null;
        return (
          <div key={lv.key} style={{ marginBottom:32 }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
              <span style={{ fontSize:22 }}>{lvData.unlocked?lv.icon:"🔒"}</span>
              <div>
                <div style={{ fontWeight:800, fontSize:17, color:lvData.unlocked?C.text:C.textMuted }}>{lv.label}</div>
                <div style={{ fontSize:12, color:C.textMuted }}>{lv.desc}</div>
              </div>
              {lvData.complete && <Badge type="success">✅ Complete</Badge>}
              {!lvData.unlocked && <Badge type="locked">🔒 Locked</Badge>}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(270px,1fr))", gap:14 }}>
              {lvData.courses.map(c=>(
                <GlassCard key={c.id} onClick={()=>{ if (c.unlocked) setSel(c); }}
                  style={{ borderLeft:`3px solid ${!c.unlocked?C.border:c.passed?"#10B981":lv.color}`, cursor:c.unlocked?"pointer":"not-allowed", opacity:c.unlocked?1:0.55 }}>
                  <div style={{ padding:18 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:10 }}>
                      <div style={{ fontWeight:700, fontSize:14, color:c.unlocked?C.text:C.textMuted, flex:1, paddingRight:8 }}>{c.name}</div>
                      {!c.unlocked ? <Badge type="locked">🔒 Locked</Badge> : c.passed ? <Badge type="success">✅ Passed</Badge> : <Badge type="gray">📋 Pending</Badge>}
                    </div>
                    <div style={{ fontSize:12, color:C.textMuted, marginBottom:8 }}>{c.code} • {c.credits} credits</div>
                    {c.unlocked
                      ? <>
                          <div style={{ fontSize:12, color:C.textMuted, marginBottom:12, lineHeight:1.5 }}>{c.description}</div>
                          <div style={{ fontSize:12, color:lv.color, fontWeight:600 }}>View Course →</div>
                        </>
                      : <div style={{ fontSize:12, color:C.textMuted }}>Complete the previous course's exam to unlock.</div>
                    }
                  </div>
                </GlassCard>
              ))}
              {lvData.courses.length===0 && <div style={{ color:C.textMuted, fontSize:13, padding:16 }}>No courses in this level yet.</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── LECTURES ────────────────────────────────────────────────────────────────
export function StudentLectures({ user }) {
  const [lectures, setLectures] = useState([]);
  const [modules, setModules] = useState([]);
  const [courses, setCourses] = useState([]);
  const [examResults, setExamResults] = useState([]);
  const [sel, setSel] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [l, m, c, r] = await Promise.all([academicAPI.getLectures(), academicAPI.getModules(), academicAPI.getCourses(), assessmentAPI.myResults()]);
        setLectures(l.data||[]); setModules(m.data||[]); setCourses(c.data||[]); setExamResults(r.data||[]);
      } catch(e) { console.error(e); }
    };
    load();
  }, []);

  // Only show lectures whose module belongs to a course the student has
  // access to (own program, or a shared "general" course for their degree)
  // — the API itself returns every lecture in the system, unfiltered.
  const access = user.program_id ? getCourseAccess(user, courses, examResults) : null;
  const moduleCourseUnlocked = new Map((access?.all||[]).map(c=>[c.id, c.unlocked]));
  const myCourseIds = access ? access.all.map(c=>c.id) : [];
  const myModuleIds = modules.filter(m=>myCourseIds.includes(m.course_id)).map(m=>m.id);
  const isModuleUnlocked = (moduleId) => {
    const mod = modules.find(m=>m.id===moduleId);
    return mod ? moduleCourseUnlocked.get(mod.course_id) : false;
  };
  const myLectures = lectures.filter(l=>myModuleIds.includes(l.module_id));

  const embedUrl = (url) => {
    if (!url) return "";
    const m = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
    return m ? `https://www.youtube.com/embed/${m[1]}` : url;
  };

  if (sel) {
    return (
      <div>
        <button onClick={()=>setSel(null)} style={{ ...S.btn("ghost","sm"), marginBottom:20, color:"#3B82F6" }}>← Back</button>
        <div style={S.card}>
          <div style={{ padding:"20px 26px", borderBottom:`1px solid ${C.border}` }}>
            <h2 style={{ margin:"0 0 6px", fontSize:18, fontWeight:800, color:C.text }}>{sel.title}</h2>
            <div style={{ display:"flex", gap:8 }}>
              {sel.module_name && <Badge type="gray">📦 {sel.module_name}</Badge>}
              {sel.course_level && <LevelBadge level={sel.course_level} />}
              <Badge type={sel.type==="youtube"?"danger":"success"}>{sel.type==="youtube"?"YouTube":"Upload"}</Badge>
            </div>
            {sel.description && <p style={{ margin:"12px 0 0", fontSize:14, color:"#64748B" }}>{sel.description}</p>}
          </div>
          <div style={{ padding:24 }}>
            {sel.type==="youtube"
              ? <div style={{ borderRadius:14, overflow:"hidden", background:"#000", aspectRatio:"16/9" }}>
                  <iframe src={embedUrl(sel.url)} width="100%" height="100%" style={{ border:"none", display:"block" }} allowFullScreen title={sel.title} />
                </div>
              : <div>
                  {sel.video_url && (
                    <div style={{ borderRadius:14, overflow:"hidden", background:"#000", aspectRatio:"16/9", marginBottom:16 }}>
                      <video src={sel.video_url} controls style={{ width:"100%", height:"100%", display:"block" }} />
                    </div>
                  )}
                  {sel.pdf_url && (
                    <div style={{ padding:"18px 22px", background:"#EFF6FF", borderRadius:12, border:"1px solid #DBEAFE", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                        <span style={{ fontSize:28 }}>📄</span>
                        <div>
                          <div style={{ fontWeight:700, color:C.text, fontSize:14 }}>{sel.pdf_name||"Lecture PDF"}</div>
                          <div style={{ fontSize:12, color:C.textMuted }}>Lecture notes / slides</div>
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:10 }}>
  <a href={sel.pdf_url} target="_blank" rel="noopener noreferrer"
    style={{ ...S.btn("primary","md"), textDecoration:"none" }}>
    👁️ View PDF
  </a>
  <a href={sel.pdf_url} download={sel.pdf_name||"lecture.pdf"}
    style={{ ...S.btn("glass","md"), textDecoration:"none" }}>
    📥 Download
  </a>
</div>
                    </div>
                  )}
                  {!sel.video_url && !sel.pdf_url && (
                    <div style={{ background:C.surfaceAlt, borderRadius:14, padding:48, textAlign:"center", border:`2px dashed ${C.border}` }}>
                      <div style={{ fontSize:52, marginBottom:12 }}>🎬</div>
                      <div style={{ fontSize:16, fontWeight:600, color:C.text }}>No media uploaded yet</div>
                    </div>
                  )}
                </div>
            }
          </div>
        </div>
      </div>
    );
  }

  const levelConfig = [
    { key:"general",    label:"General",    icon:"🔵" },
    { key:"core",       label:"Core",       icon:"🟣" },
    { key:"specialist", label:"Specialist", icon:"🟡" },
  ];

  return (
    <div>
      <PageHdr title="My Lectures" sub={`${myLectures.length} lectures available`} />
      {levelConfig.map(lv=>{
        if ((access?.[lv.key]?.courses.length||0)===0) return null;
        const lvLectures = myLectures.filter(l=>l.course_level===lv.key);
        return (
          <div key={lv.key} style={{ marginBottom:32 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
              <span style={{ fontSize:20 }}>{lv.icon}</span>
              <div style={{ fontWeight:800, fontSize:16, color:C.text }}>{lv.label} Lectures</div>
              <Badge type="gray">{lvLectures.length}</Badge>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(270px,1fr))", gap:16 }}>
              {lvLectures.map(l=>{
                const thumbId = l.url?.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/)?.[1];
                const unlocked = isModuleUnlocked(l.module_id);
                return (
                  <GlassCard key={l.id} onClick={()=>{ if (unlocked) setSel(l); }} style={{ cursor:unlocked?"pointer":"not-allowed", opacity:unlocked?1:0.55 }}>
                    <div style={{ aspectRatio:"16/9", background:unlocked&&thumbId?`url(https://img.youtube.com/vi/${thumbId}/mqdefault.jpg) center/cover`:G.blue, display:"flex", alignItems:"center", justifyContent:"center", position:"relative" }}>
                      <div style={{ width:50, height:50, background:"rgba(255,255,255,0.9)", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>{unlocked?"▶":"🔒"}</div>
                      <div style={{ position:"absolute", top:8, right:8 }}>{unlocked ? <Badge type={l.type==="youtube"?"danger":"info"}>{l.type==="youtube"?"YT":"Upload"}</Badge> : <Badge type="locked">🔒 Locked</Badge>}</div>
                    </div>
                    <div style={{ padding:14 }}>
                      <div style={{ fontWeight:700, fontSize:13, color:C.text, marginBottom:4 }}>{l.title}</div>
                      <div style={{ fontSize:12, color:C.textMuted }}>{unlocked ? l.module_name : "Complete the previous course's exam to unlock"}</div>
                    </div>
                  </GlassCard>
                );
              })}
              {lvLectures.length===0 && <div style={{ color:C.textMuted, fontSize:13, padding:16 }}>No lectures in this level yet.</div>}
            </div>
          </div>
        );
      })}
      {myLectures.length===0 && <Empty icon="🎬" text="No lectures yet" />}
    </div>
  );
}

// ── ASSIGNMENTS ─────────────────────────────────────────────────────────────
export function StudentAssignments({ user }) {
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [submitModal, setSubmitModal] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const [a, s, c] = await Promise.all([assessmentAPI.getAssignments(), assessmentAPI.mySubmissions(), academicAPI.getCourses()]);
      setAssignments(a.data||[]); setSubmissions(s.data||[]); setCourses(c.data||[]);
    } catch(e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!pdfFile) { alert("Select a PDF file."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("pdf", pdfFile);
      await assessmentAPI.submitAssignment(submitModal.id, fd);
      setSubmitModal(null); setPdfFile(null); load();
    } catch(e) { alert(e.message); }
    finally { setUploading(false); }
  };

  // The API returns every assignment in the system unfiltered — only show
  // the ones whose course the student has access to.
  const myCourseIds = user.program_id ? courses.filter(c=>isMyCourse(c,user)).map(c=>c.id) : [];
  const myAssignments = assignments.filter(a=>myCourseIds.includes(a.course_id));

  return (
    <div>
      <PageHdr title="My Assignments" sub={`${myAssignments.length} total`} />
      {myAssignments.map(a=>{
        const sub = submissions.find(s => s.assignment_id===a.id);
        const past = a.due_date && new Date(a.due_date) < new Date();
        const days = !past ? Math.ceil((new Date(a.due_date)-new Date())/86400000) : 0;
        const borderColor = sub?"#10B981":past?"#F43F5E":"#F59E0B";
        return (
          <div key={a.id} style={{ ...S.card, marginBottom:14, borderLeft:`3px solid ${borderColor}` }}>
            <div style={{ padding:"18px 22px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", gap:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap", marginBottom:8 }}>
                    <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:C.text }}>{a.title}</h3>
                    <Badge type={sub?(sub.status==="graded"?"success":"info"):past?"danger":"warning"}>
                      {sub?sub.status.replace("-"," "):past?"overdue":"pending"}
                    </Badge>
                    {a.course_level && <LevelBadge level={a.course_level} />}
                  </div>
                  <p style={{ margin:"0 0 10px", fontSize:13, color:C.textMuted }}>{a.description}</p>
                  <div style={{ display:"flex", gap:14, fontSize:12, color:C.textMuted, flexWrap:"wrap" }}>
                    {a.course_name && <span>📚 {a.course_name}</span>}
                    <span>📅 {a.due_date?new Date(a.due_date).toLocaleDateString("en-PK"):"—"}</span>
                    {!past && !sub && <span style={{ color:days<=3?"#F43F5E":"#F59E0B" }}>⏰ {days}d left</span>}
                  </div>
                  {a.pdf_url && (
                    <a href={a.pdf_url} download={a.pdf_name||"assignment.pdf"} target="_blank" rel="noopener noreferrer"
                      style={{ ...S.btn("glass","sm"), marginTop:10, textDecoration:"none" }}>
                      📄 Download Question Paper
                    </a>
                  )}
                  {sub?.marks!=null && (
                    <div style={{ marginTop:12, display:"inline-flex", alignItems:"center", gap:10, padding:"10px 16px", background:C.emeraldLight, borderRadius:10 }}>
                      <span style={{ fontWeight:800, color:"#059669", fontSize:20 }}>{sub.marks}%</span>
                      {sub.feedback && <span style={{ fontSize:12, color:C.textMuted }}>{sub.feedback}</span>}
                    </div>
                  )}
                  {sub?.status==="auto-submitted" && <div style={{ marginTop:8, fontSize:12, color:"#F59E0B" }}>⚡ Auto-submitted on due date.</div>}
                </div>
                <div style={{ flexShrink:0 }}>
                  {!sub && !past && <button style={S.btn("primary")} onClick={()=>{ setSubmitModal(a); setPdfFile(null); }}>📤 Submit</button>}
                </div>
              </div>
            </div>
          </div>
        );
      })}
      {myAssignments.length===0 && <Empty icon="📝" text="No assignments yet" />}
      <Modal open={!!submitModal} onClose={()=>setSubmitModal(null)} title={`Submit: ${submitModal?.title}`}
        footer={<><button style={S.btn("ghost")} onClick={()=>setSubmitModal(null)}>Cancel</button><button style={S.btn("primary")} onClick={submit} disabled={uploading}>{uploading?"Uploading...":"📤 Submit"}</button></>}>
        <div style={S.alert("info")}>Upload your completed assignment as PDF.</div>
        <div style={S.formGroup}>
          <label style={S.label}>Select PDF *</label>
          <input type="file" accept=".pdf" onChange={e=>setPdfFile(e.target.files[0])} style={{ color:C.textMuted, fontSize:13, display:"block", padding:"8px 0" }} />
          {pdfFile && <div style={{ fontSize:13, color:"#10B981", marginTop:8 }}>✅ {pdfFile.name}</div>}
        </div>
      </Modal>
    </div>
  );
}

// ── EXAMS ────────────────────────────────────────────────────────────────────
export function StudentExams({ user }) {
  const [exams, setExams] = useState([]);
  const [myResults, setMyResults] = useState([]);
  const [courses, setCourses] = useState([]);
  const [activeExam, setActiveExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const [e, r, c] = await Promise.all([assessmentAPI.getExams(), assessmentAPI.myResults(), academicAPI.getCourses()]);
      setExams(e.data||[]); setMyResults(r.data||[]); setCourses(c.data||[]);
    } catch(e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!activeExam || timeLeft===null) return;
    timerRef.current = setInterval(()=>{
      setTimeLeft(t=>{ if(t<=1){ clearInterval(timerRef.current); doSubmit(); return 0; } return t-1; });
    }, 1000);
    return ()=>clearInterval(timerRef.current);
  }, [activeExam]);

  const access = user.program_id ? getCourseAccess(user, courses, myResults) : null;
  const courseUnlocked = new Map((access?.all||[]).map(c=>[c.id, c.unlocked]));
  const myCourseIds = access ? access.all.map(c=>c.id) : [];
  const myExams = exams.filter(e=>myCourseIds.includes(e.course_id));

  const start = (exam) => {
    if (myResults.find(r=>r.exam_id===exam.id)) { alert("Already taken!"); return; }
    if (!courseUnlocked.get(exam.course_id)) { alert("Complete the previous course's exam to unlock this one."); return; }
    setActiveExam(exam); setAnswers({}); setTimeLeft(exam.duration*60); setResult(null);
  };

  const doSubmit = async () => {
    clearInterval(timerRef.current);
    const exam = activeExam;
    try {
      const res = await assessmentAPI.submitExam(exam.id, answers);
      setResult({ res:res.data, exam }); setActiveExam(null); load();
    } catch(e) { alert(e.message); }
  };

  const fmt = s=>`${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;

  if (result) return (
    <div style={{ maxWidth:520, margin:"0 auto", textAlign:"center", paddingTop:20 }}>
      <div style={{ fontSize:72, marginBottom:12 }}>🎓</div>
      <h2 style={{ fontSize:26, fontWeight:800, color:"#10B981", marginBottom:6 }}>Exam Submitted!</h2>
      <div style={{ ...S.card, padding:32, marginBottom:22 }}>
        <div style={{ fontSize:72, fontWeight:900, color:"#10B981", lineHeight:1 }}>{result.res.final_score}%</div>
        <div style={{ margin:"10px 0 14px" }}><Badge type="success">✓ PASSED</Badge></div>
        {result.res.score<60 && <div style={S.alert("warning")}>⚡ Raw: {result.res.score}% → Auto-boosted to 60%</div>}
      </div>
      <button style={S.btn("primary")} onClick={()=>setResult(null)}>← Back</button>
    </div>
  );

  if (activeExam) return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:22 }}>
        <h2 style={S.pageTitle}>{activeExam.title}</h2>
        <div style={{ display:"flex", gap:12 }}>
          <div style={{ padding:"9px 18px", background:timeLeft<60?C.roseLight:"#DBEAFE", color:timeLeft<60?"#E11D48":"#1D4ED8", borderRadius:10, fontWeight:800, fontSize:20, fontFamily:"monospace" }}>⏱ {fmt(timeLeft)}</div>
          <button style={S.btn("primary")} onClick={doSubmit}>Submit</button>
        </div>
      </div>
      {(activeExam.questions||[]).map((q,i)=>(
        <div key={q.id||i} style={{ ...S.card, marginBottom:14 }}>
          <div style={{ padding:22 }}>
            <div style={{ fontWeight:700, fontSize:15, color:C.text, marginBottom:16 }}>Q{i+1}. {q.question}</div>
            {(q.options||(q.options?JSON.parse(q.options):[])).map((opt,j)=>(
              <label key={j} style={{ display:"flex", alignItems:"center", gap:12, padding:"11px 16px", borderRadius:10, border:`2px solid ${answers[q.id||i]===j?C.navy:C.border}`, background:answers[q.id||i]===j?"#DBEAFE":"transparent", cursor:"pointer", marginBottom:8, color:answers[q.id||i]===j?C.navy:C.textMid, fontWeight:answers[q.id||i]===j?600:400 }}>
                <input type="radio" name={q.id||i} checked={answers[q.id||i]===j} onChange={()=>setAnswers({...answers,[q.id||i]:j})} style={{ display:"none" }} />
                <span style={{ width:20, height:20, borderRadius:"50%", border:`2px solid ${answers[q.id||i]===j?C.navy:C.border}`, background:answers[q.id||i]===j?C.navy:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  {answers[q.id||i]===j && <span style={{ width:7, height:7, background:C.white, borderRadius:"50%", display:"block" }} />}
                </span>
                {opt}
              </label>
            ))}
          </div>
        </div>
      ))}
      <div style={{ textAlign:"center", marginTop:20 }}>
        <button style={{ ...S.btn("primary","lg"), minWidth:180 }} onClick={doSubmit}>📋 Submit Exam</button>
      </div>
    </div>
  );

  const levelConfig = [
    { key:"general",    label:"General",    icon:"🔵" },
    { key:"core",       label:"Core",       icon:"🟣" },
    { key:"specialist", label:"Specialist", icon:"🟡" },
  ];

  return (
    <div>
      <PageHdr title="My Exams" sub="Min 60% guaranteed" />
      {levelConfig.map(lv=>{
        if ((access?.[lv.key]?.courses.length||0)===0) return null;
        const lvExams = myExams.filter(e=>e.course_level===lv.key);
        return (
          <div key={lv.key} style={{ marginBottom:32 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
              <span style={{ fontSize:20 }}>{lv.icon}</span>
              <div style={{ fontWeight:800, fontSize:16, color:C.text }}>{lv.label} Exams</div>
              <Badge type="gray">{lvExams.length}</Badge>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:16 }}>
              {lvExams.map(e=>{
                const res = myResults.find(r=>r.exam_id===e.id);
                const unlocked = courseUnlocked.get(e.course_id);
                return (
                  <GlassCard key={e.id} style={{ opacity:unlocked?1:0.55 }}>
                    <div style={{ padding:22 }}>
                      <div style={{ width:46, height:46, background:res?C.emeraldLight:unlocked?"#DBEAFE":C.surfaceAlt, borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, marginBottom:14 }}>{res?"✅":unlocked?"📋":"🔒"}</div>
                      <h3 style={{ margin:"0 0 5px", fontSize:14, fontWeight:700, color:C.text }}>{e.title}</h3>
                      {e.course_level && <div style={{ marginBottom:8 }}><LevelBadge level={e.course_level} /></div>}
                      <p style={{ margin:"0 0 14px", fontSize:12, color:"#64748B" }}>{e.course_name}</p>
                      <div style={{ display:"flex", gap:8, marginBottom:16 }}>
                        <Badge type="info">{(e.questions||[]).length}Q</Badge>
                        <Badge type="gray">⏱ {e.duration}min</Badge>
                      </div>
                      {res ? (
                        <div style={{ padding:"12px 0", textAlign:"center" }}>
                          <div style={{ fontSize:32, fontWeight:900, color:"#10B981" }}>{res.final_score}%</div>
                          <Badge type="success">PASSED ✓</Badge>
                        </div>
                      ) : !unlocked ? (
                        <div style={{ padding:"10px 0", textAlign:"center" }}><Badge type="locked">🔒 Locked</Badge></div>
                      ) : <button style={{ ...S.btn("primary"), width:"100%", justifyContent:"center" }} onClick={()=>start(e)}>Start Exam →</button>}
                    </div>
                  </GlassCard>
                );
              })}
              {lvExams.length===0 && <div style={{ color:C.textMuted, fontSize:13, padding:16 }}>No exams in this level yet.</div>}
            </div>
          </div>
        );
      })}
      {myExams.length===0 && <Empty icon="📋" text="No exams yet" />}
    </div>
  );
}

// ── GRADES ────────────────────────────────────────────────────────────────
export function StudentGrades({ user }) {
  const [submissions, setSubmissions] = useState([]);
  const [examResults, setExamResults] = useState([]);
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, r, a] = await Promise.all([assessmentAPI.mySubmissions(), assessmentAPI.myResults(), assessmentAPI.getAssignments()]);
        setSubmissions(s.data||[]); setExamResults(r.data||[]); setAssignments(a.data||[]);
      } catch(e) { console.error(e); }
    };
    load();
  }, []);

  const gradedSubs = submissions.filter(s=>s.marks!=null);
  const avgA = gradedSubs.length>0 ? Math.round(gradedSubs.reduce((a,s)=>a+s.marks,0)/gradedSubs.length) : null;
  const avgE = examResults.length>0 ? Math.round(examResults.reduce((a,r)=>a+r.final_score,0)/examResults.length) : null;
  const overall = avgA!=null&&avgE!=null ? Math.round((avgA+avgE)/2) : avgA??avgE;
  const grade = calcGrade(overall);

  return (
    <div>
      <PageHdr title="My Grades" />
      {overall!=null && (
        <div style={{ background:G.blue, borderRadius:16, padding:"24px 30px", marginBottom:24, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:14, color:"rgba(255,255,255,0.7)", marginBottom:4 }}>Overall Performance</div>
            <div style={{ fontSize:36, fontWeight:900, color:C.white }}>{overall}%</div>
            <div style={{ fontSize:13, color:"rgba(255,255,255,0.6)", marginTop:4 }}>Assignments: {avgA??"—"}% | Exams: {avgE??"—"}%</div>
          </div>
          <div style={{ textAlign:"center" }}>
            <div style={{ fontSize:64, fontWeight:900, color:C.white, lineHeight:1 }}>{grade}</div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.6)" }}>Final Grade</div>
          </div>
        </div>
      )}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
        <div style={S.card}>
          <div style={{ padding:"13px 18px", borderBottom:`1px solid ${C.border}`, fontWeight:700, color:C.text }}>📝 Assignments</div>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Assignment</th><th style={S.th}>Marks</th><th style={S.th}>Status</th></tr></thead>
            <tbody>
              {assignments.map(a=>{
                const sub = submissions.find(s=>s.assignment_id===a.id);
                return <tr key={a.id}><td style={S.td}>{a.title}</td><td style={S.td}>{sub?.marks!=null?<strong style={{ color:"#10B981" }}>{sub.marks}%</strong>:<Badge type="gray">—</Badge>}</td><td style={S.td}><Badge type={sub?(sub.status==="graded"?"success":"warning"):"gray"}>{sub?sub.status:"—"}</Badge></td></tr>;
              })}
              {assignments.length===0 && <tr><td colSpan={3} style={{ ...S.td, textAlign:"center", color:C.textMuted }}>No assignments yet</td></tr>}
            </tbody>
          </table>
        </div>
        <div style={S.card}>
          <div style={{ padding:"13px 18px", borderBottom:`1px solid ${C.border}`, fontWeight:700, color:C.text }}>📋 Exams</div>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Exam</th><th style={S.th}>Score</th><th style={S.th}>Level</th></tr></thead>
            <tbody>
              {examResults.length===0 ? <tr><td colSpan={3} style={{ ...S.td, textAlign:"center", color:C.textMuted }}>No exams yet</td></tr>
                : examResults.map(r=>(
                  <tr key={r.id}>
                    <td style={S.td}>{r.exam_title}</td>
                    <td style={S.td}><strong style={{ color:"#10B981" }}>{r.final_score}%</strong>{r.score<60&&<span style={{ fontSize:9, color:"#F59E0B" }}> ⚡</span>}</td>
                    <td style={S.td}>{r.course_level&&<LevelBadge level={r.course_level} />}</td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── SETTINGS ────────────────────────────────────────────────────────────────
export function StudentSettings() {
  const [form, setForm] = useState({ currentPassword:"", newPassword:"", confirmPassword:"" });
  const [msg, setMsg] = useState(null); // { type: "success"|"error", text }
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setMsg(null);
    if (!form.currentPassword || !form.newPassword || !form.confirmPassword) {
      setMsg({ type:"error", text:"Please fill all fields." }); return;
    }
    if (form.newPassword.length < 6) {
      setMsg({ type:"error", text:"New password must be at least 6 characters." }); return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setMsg({ type:"error", text:"New password and confirmation do not match." }); return;
    }
    setSaving(true);
    try {
      await authAPI.changePassword(form.currentPassword, form.newPassword);
      setMsg({ type:"success", text:"Password updated successfully." });
      setForm({ currentPassword:"", newPassword:"", confirmPassword:"" });
    } catch(e) { setMsg({ type:"error", text:e.message }); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHdr title="Change Password" sub="Update the password you use to sign in" />
      <div style={{ ...S.card, maxWidth:440, padding:"24px 26px" }}>
        {msg && <div style={S.alert(msg.type==="success"?"success":"danger")}>{msg.type==="success"?"✅ ":"⚠️ "}{msg.text}</div>}
        <Inp label="Current Password *" type="password" value={form.currentPassword} onChange={e=>setForm({...form,currentPassword:e.target.value})} />
        <Inp label="New Password *" type="password" value={form.newPassword} onChange={e=>setForm({...form,newPassword:e.target.value})} />
        <Inp label="Confirm New Password *" type="password" value={form.confirmPassword} onChange={e=>setForm({...form,confirmPassword:e.target.value})} />
        <button style={{ ...S.btn("primary"), width:"100%", justifyContent:"center", marginTop:6 }} onClick={save} disabled={saving}>
          {saving ? "Saving..." : "🔒 Update Password"}
        </button>
      </div>
    </div>
  );
}

// ── CONTROL PANEL (Re-apply Degree) ──────────────────────────────────────────
export function StudentControlPanel({ user }) {
  const [enteredStudentId, setEnteredStudentId] = useState("");
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setMsg(null);
    if (!enteredStudentId.trim() || !reason.trim()) {
      setMsg({ type:"error", text:"Please fill in your Student ID and a reason." }); return;
    }
    setSaving(true);
    try {
      await reapplyAPI.submit({ entered_student_id: enteredStudentId.trim(), reason: reason.trim() });
      setEnteredStudentId(""); setReason(""); setDone(true);
    } catch(e) { setMsg({ type:"error", text:e.message }); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHdr title="Control Panel" sub="Re-apply for your degree" />
      <div style={{ ...S.card, maxWidth:480, padding:"24px 26px" }}>
        {msg && <div style={S.alert("danger")}>⚠️ {msg.text}</div>}
        <Inp label="Full Name" value={user.name} disabled readOnly />
        <Inp label="Program" value={user.program_name || "—"} disabled readOnly />
        <Inp label="Student ID *" placeholder="Enter your Student ID" value={enteredStudentId} onChange={e=>setEnteredStudentId(e.target.value)} />
        <div style={S.formGroup}>
          <label style={S.label}>Reason *</label>
          <textarea style={{ ...S.input, minHeight:100, resize:"vertical" }} placeholder="Why do you want to re-apply for your degree?" value={reason} onChange={e=>setReason(e.target.value)} />
        </div>
        <button style={{ ...S.btn("primary"), width:"100%", justifyContent:"center", marginTop:6 }} onClick={submit} disabled={saving}>
          {saving ? "Submitting..." : "📤 Submit Application"}
        </button>
      </div>
      <Modal open={done} onClose={()=>setDone(false)} title="✅ Application Submitted"
        footer={<button style={{ ...S.btn("primary"), width:"100%", justifyContent:"center" }} onClick={()=>setDone(false)}>OK</button>}>
        <div style={{ textAlign:"center", padding:"12px 0" }}>
          <div style={{ fontSize:48, marginBottom:14 }}>🎓</div>
          <div style={{ fontSize:15, color:C.textMid, lineHeight:1.6 }}>We will get back to you in 48 to 72 hrs.</div>
        </div>
      </Modal>
    </div>
  );
}

// ── ANNOUNCEMENTS ──────────────────────────────────────────────────────────
export function StudentAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    const load = async () => {
      try { const a = await commAPI.getAnnouncements(); setAnnouncements(a.data||[]); } catch(e) { console.error(e); }
    };
    load();
  }, []);

  return (
    <div>
      <PageHdr title="Announcements" />
      {announcements.length===0 ? <Empty icon="📢" text="No announcements" /> : announcements.map(a=>(
        <div key={a.id} style={{ ...S.card, marginBottom:14, borderLeft:"3px solid #3B82F6" }}>
          <div style={{ padding:"18px 22px" }}>
            <div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:8, flexWrap:"wrap" }}>
              <span style={{ fontSize:18 }}>📢</span>
              <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:C.text }}>{a.title}</h3>
            </div>
            <p style={{ margin:"0 0 10px", fontSize:14, color:C.textMid, lineHeight:1.7 }}>{a.message}</p>
            <div style={{ fontSize:12, color:C.textMuted }}>{new Date(a.created_at).toLocaleDateString("en-PK")}</div>
          </div>
        </div>
      ))}
    </div>
  );
}